import { Button, Checkbox, Group, Modal, Stack } from "@mantine/core";
import { truncateAnalyticsText, useReactAnalytics } from "@soluto-private/eventualize-react";
import { useState } from "react";

interface GuideCloseConfirmationProps {
    callSid?: string;
    extraData?: Record<string, unknown>;
    guideId: string;
    isOpen: boolean;
    options: string[];
    title: string;
    onCancel: () => void;
    onClose: () => void;
}

function buttonCssVarOverrides() {
    return () => {
        return {
            root: {
                "--button-bg": "var(--mantine-color-primary-6)",
                "--button-hover": "var(--mantine-color-primary-7)",
            },
        };
    };
}
export function GuideCloseConfirmation({
    callSid,
    extraData,
    guideId,
    isOpen,
    onCancel,
    onClose,
    options,
    title,
}: GuideCloseConfirmationProps) {
    const { dispatcher } = useReactAnalytics();

    const enhancedDispatcher = callSid ? dispatcher.withIdentities({ CallSid: callSid }) : dispatcher;

    const [selectedFeedbackOptions, setSelectedFeedbackOptions] = useState<string[]>([]);

    const handleSubmit = () => {
        try {
            void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_SubmitAndCloseGuide`, {
                button: "SubmitFeedbackAndCloseGuide",
                guideId,
                feedbackReasons: selectedFeedbackOptions,
                ...extraData,
            });
            void enhancedDispatcher.dispatchBusinessEvent("SubmitFeedback", {
                feedbackCategory: `Solve Guide - ${guideId}`,
                feedbackText: truncateAnalyticsText(
                    `Closed guide for the following reason(s): ${selectedFeedbackOptions.join(", ")}`,
                ),
            });
            onClose();
        } catch {
            onCancel();
        }
    };

    const handleCancel = () => {
        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_CancelCloseGuide`, {
            button: "CancelCloseGuide",
            ...extraData,
        });
        onCancel();
    };

    const handleClose = () => {
        void enhancedDispatcher.dispatchBusinessEvent(`Click_Guide_ConfirmCloseGuide`, {
            button: "ConfirmCloseGuide",
            ...extraData,
        });
        onClose();
    };

    return (
        <Modal.Root
            opened={isOpen}
            onClose={() => {
                /* onClose is handled through the close/cancel/submit buttons below */
            }}
            zIndex="var(--mantine-priority-highest)"
            centered
        >
            <Modal.Content>
                <Modal.Header styles={{ header: { alignItems: "unset" } }}>
                    <Modal.Title p="md" pb="0">
                        {title}
                    </Modal.Title>
                    <Modal.CloseButton onClick={handleClose} />
                </Modal.Header>
                <Modal.Body>
                    <Stack gap="1.25rem">
                        <Stack gap="1.25rem">
                            <Checkbox.Group value={selectedFeedbackOptions} onChange={setSelectedFeedbackOptions}>
                                {options.map((option) => (
                                    <Checkbox value={option} label={option} key={option} p="xxs" />
                                ))}
                            </Checkbox.Group>
                        </Stack>
                        <Group justify="flex-end">
                            <Button
                                bg="transparent"
                                color="dark.1"
                                data-testid="cancel-feedback-button"
                                onClick={handleCancel}
                                size="md"
                                td="underline"
                                variant="subtle"
                            >
                                Cancel
                            </Button>
                            <Button
                                size="md"
                                onClick={handleSubmit}
                                data-testid="submit-and-close-button"
                                variant="filled"
                                vars={buttonCssVarOverrides()}
                            >
                                Submit and close
                            </Button>
                        </Group>
                    </Stack>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
}
